import React from "react";
import {
  createBulkSubstatusAction,
  type CreateBulkSubstatusActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import type { BulkActionType } from "../activity/labels";
import type { BulkTargets } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import { useBulkActionProgressToast } from "./useBulkActionProgressToast";
import { useRequireBulkTargets } from "./useRequireBulkTargets";

export type BulkSubstatusActionType = NonNullable<
  CreateBulkSubstatusActionData["body"]
>["data"]["action"];

type EnqueueBulkSubstatusArgs = {
  action: BulkSubstatusActionType;
};

function createBulkSubstatusBody(
  action: BulkSubstatusActionType,
  targets: BulkTargets
): CreateBulkSubstatusActionData["body"] {
  return { data: { ...targets, action } };
}

export function useEnqueueBulkSubstatus() {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();
  const { clearSelection, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();

  return React.useCallback(
    async ({ action }: EnqueueBulkSubstatusArgs): Promise<boolean> => {
      try {
        const targets = await requireTargets();
        if (!targets) {
          return false;
        }

        const response = throwIfFormServiceError(
          await createBulkSubstatusAction({
            body: createBulkSubstatusBody(action, targets),
          })
        );

        const actionType: BulkActionType =
          action === "EXCLUDE" ? "EXCLUDE_FROM_OFFERS" : "CLEAR_SUBSTATUS";
        trackFormAction({
          verb: "Queued",
          actionType,
          bulk: true,
          count: selectionSize,
          async: true,
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: targets.formTemplateId,
          actionType,
          selectionSize,
        });
        clearSelection();
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType:
            action === "EXCLUDE" ? "EXCLUDE_FROM_OFFERS" : "CLEAR_SUBSTATUS",
          bulk: true,
          count: selectionSize,
          async: true,
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [clearSelection, requireTargets, selectionSize, showProgressToast, toast]
  );
}
